import { useMutation } from '@apollo/client';
import {
  ResponsiveGridLayouts,
  useSettings,
} from '@jupiterone/jupiter-web-toolkit';
import { SetSettings } from '@jupiterone/jupiter-web-toolkit/src/graphql/queries/J1';
import {
  HOME_DASHBOARD_CATEGORY,
  LAYOUTS_CATEGORY,
  USER_AND_TEAM_DASHBOARDS_CATEGORY,
  WIDGETS_CATEGORY,
} from '../constants';
import {
  HOME_INSIGHTS_BOARD,
  HOME_WIDGETS,
  HOME_WIDGET_LAYOUTS,
} from '../boards/home';
import defaultBoards from '../boards/defaultTeamBoards';
import { BoardType, InsightsBoard, Widget } from '../types';

export default function useBoards() {
  // home board
  const [homeBoards, saveHomeBoards] = useSettings<InsightsBoard[]>(
    'account',
    HOME_DASHBOARD_CATEGORY
  );

  // team boards
  const [teamBoards, saveTeamBoards] = useSettings<InsightsBoard[]>(
    'account',
    USER_AND_TEAM_DASHBOARDS_CATEGORY
  );

  // user boards
  const [userBoards, saveUserBoards] = useSettings<InsightsBoard[]>(
    'user',
    USER_AND_TEAM_DASHBOARDS_CATEGORY
  );

  const [mutateSetSettings] = useMutation(SetSettings);

  const saveWidgetsBoard = (
    resourceType: 'account' | 'user',
    boardId: string,
    widgetsBoard: Widget[]
  ) => {
    return mutateSetSettings({
      variables: {
        resourceType,
        data: widgetsBoard,
        category: `${WIDGETS_CATEGORY}/${boardId}`,
      },
    });
  };

  const saveLayouts = (
    resourceType: 'account' | 'user',
    boardId: string,
    layouts: ResponsiveGridLayouts
  ) => {
    return mutateSetSettings({
      variables: {
        resourceType,
        data: layouts,
        category: `${LAYOUTS_CATEGORY}/${boardId}`,
      },
    });
  };

  // save boards and update state
  const saveBoards = (boardType: BoardType, boards: InsightsBoard[]) => {
    if (boardType === 'user') {
      return saveUserBoards(boards);
    } else if (boardType === 'team') {
      return saveTeamBoards(boards);
    } else {
      return Promise.resolve();
    }
  };

  // initialize default boards
  const createDefaultBoards = async (teamBoards: InsightsBoard[]) => {
    const createDefaultHomeBoardData = async () => {
      return Promise.all([
        saveWidgetsBoard('account', HOME_INSIGHTS_BOARD.id, HOME_WIDGETS),
        saveLayouts('account', HOME_INSIGHTS_BOARD.id, HOME_WIDGET_LAYOUTS),
        saveLayouts('user', HOME_INSIGHTS_BOARD.id, HOME_WIDGET_LAYOUTS),
        saveHomeBoards([HOME_INSIGHTS_BOARD]),
      ]);
    };

    const createDefaultTeamBoardsData = async (teamBoards: InsightsBoard[]) => {
      return Promise.all([
        teamBoards.map((teamBoard, index) =>
          saveWidgetsBoard(
            'account',
            teamBoard.id,
            defaultBoards.DEFAULT_TEAM_WIDGETS[index]
          )
        ),
        teamBoards.map((teamBoard, index) =>
          saveLayouts(
            'account',
            teamBoard.id,
            defaultBoards.DEFAULT_TEAM_WIDGETS_LAYOUTS[index]
          )
        ),
        teamBoards.map((teamBoard, index) =>
          saveLayouts(
            'user',
            teamBoard.id,
            defaultBoards.DEFAULT_TEAM_WIDGETS_LAYOUTS[index]
          )
        ),
        saveTeamBoards(teamBoards),
      ]);
    };

    return Promise.all([
      createDefaultHomeBoardData(),
      createDefaultTeamBoardsData(teamBoards),
    ]);
  };

  return {
    loading: homeBoards.loading || teamBoards.loading || userBoards.loading,
    homeDashboard: HOME_INSIGHTS_BOARD,
    teamBoards: teamBoards.data || undefined,
    userBoards: userBoards.data || undefined,
    saveBoards,
    createDefaultBoards,
  };
}
